// M20 photo tone: the ad's second colour (besides the brand's), sampled from the dish photo. The dominant
// hue of the food: transparent pixels (a cut-out's background), near-white (plates, backdrops), near-black
// and grey pixels are skipped; the most common hue (weighted by saturation) wins, and its mean colour is
// turned into three calm variants — a mid tone, a very light tint for backgrounds, a deep shade.
import type { ToneRole } from '../templates/dsl';

type RGB = [number, number, number];

function rgbToHsl([r, g, b]: RGB): RGB {
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    l = (mx + mn) / 2,
    d = mx - mn;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = mx === r ? ((g - b) / d + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [r + m, g + m, b + m];
}

/** The three tones from RGBA pixels (0..255). Null when nothing coloured is found (e.g. a grey photo). */
export function toneFromPixels(px: Uint8ClampedArray | Uint8Array): Record<ToneRole, RGB> | null {
  const BINS = 24;
  const w = new Float64Array(BINS),
    sum = Array.from({ length: BINS }, () => [0, 0, 0]);
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3]! < 200) continue;
    const c: RGB = [px[i]! / 255, px[i + 1]! / 255, px[i + 2]! / 255];
    const [h, s, l] = rgbToHsl(c);
    if (l > 0.9 || l < 0.1 || s < 0.18) continue;
    const bin = Math.floor(h / (360 / BINS)) % BINS;
    const k = s * (1 - Math.abs(l - 0.5));
    w[bin] = w[bin]! + k;
    const acc = sum[bin]!;
    acc[0] = acc[0]! + c[0] * k;
    acc[1] = acc[1]! + c[1] * k;
    acc[2] = acc[2]! + c[2] * k;
  }
  let best = -1;
  for (let i = 0; i < BINS; i++) if (w[i]! > (best < 0 ? 0 : w[best]!)) best = i;
  if (best < 0 || w[best]! === 0) return null;
  const mean: RGB = [sum[best]![0]! / w[best]!, sum[best]![1]! / w[best]!, sum[best]![2]! / w[best]!];
  const [h, s] = rgbToHsl(mean);
  // Calm, usable versions of the food's own colour: never neon, never muddy.
  return {
    tone: hslToRgb(h, Math.min(0.55, Math.max(0.3, s * 0.75)), 0.45),
    toneLight: hslToRgb(h, Math.min(0.45, Math.max(0.2, s * 0.5)), 0.93),
    toneDark: hslToRgb(h, Math.min(0.35, Math.max(0.15, s * 0.4)), 0.13),
  };
}

/** The tones of an image file (downscaled to 96 px first). */
export async function toneFromImage(bytes: Uint8Array, type: string): Promise<Record<ToneRole, RGB> | null> {
  const bmp = await createImageBitmap(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  const k = Math.min(1, 96 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * k)),
    h = Math.max(1, Math.round(bmp.height * k));
  const c = new OffscreenCanvas(w, h);
  const g = c.getContext('2d')!;
  g.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  return toneFromPixels(g.getImageData(0, 0, w, h).data);
}
